import Link from "next/link";
import Image from "next/image";
import { cn } from "@/lib/utils";

type LogoVariant = "full" | "mark";

interface LogoProps {
  variant?: LogoVariant;
  href?: string;
  className?: string;
  size?: number;
}

/**
 * CrewAhead logo mark + wordmark.
 *
 * `variant="full"` renders the mark alongside the wordmark.
 * `variant="mark"` renders only the icon for compact contexts (e.g. a collapsed sidebar).
 *
 * The mark is loaded from /public/brand/logo.svg so swapping the file updates every
 * surface without further code changes.
 */
export default function Logo({
  variant = "full",
  href = "/",
  className,
  size = 36,
}: LogoProps) {
  const mark = (
    <Image
      src="/brand/logo.svg"
      alt="CrewAhead"
      width={size}
      height={size}
      className={cn("shrink-0", variant === "full" && "h-9 w-9")}
      priority
    />
  );

  const content =
    variant === "full" ? (
      <div className={cn("flex items-center gap-2.5", className)}>
        {mark}
        <span className="font-display text-xl font-bold tracking-tight text-gradient-brand">
          CrewAhead
        </span>
      </div>
    ) : (
      <div className={className}>{mark}</div>
    );

  if (href) {
    return (
      <Link href={href} aria-label="CrewAhead home" className="inline-flex min-h-11 items-center">
        {content}
      </Link>
    );
  }

  return content;
}
